import { useMutation } from '@apollo/client';
import moment from 'moment';
import { CalendarIcon, DeleteIcon, ExternalLinkIcon, RepeatIcon, TimeIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Center,
    Flex,
    HStack,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverFooter,
    PopoverHeader,
    PopoverTrigger,
    Select,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { UPDATE_INSTANCE_MUTATION } from '../api/instances-api';

type InstanceVarsPopoverProps = { instance: PipelineInstance };
function InstanceVarsPopover({ instance }: InstanceVarsPopoverProps) {
    const [serachQuery, setSearchQuery] = useState('');
    const [changes, setChanges] = useState({} as any);

    const [updateInstance] = useMutation(UPDATE_INSTANCE_MUTATION, {
        onCompleted: (data) => {
            const instanceId = data?.response?.id;
            if (!instanceId) {
                alert("Error: didn't get id");
            }
        },
        onError: (err) => {
            alert(err);
        },
    });

    return (
        <Popover isLazy placement={'auto-end'}>
            <PopoverTrigger>
                <Button mr={2}>Vars</Button>
            </PopoverTrigger>
            <PopoverContent backdropBlur={'lg'} w={'600px'}>
                <PopoverArrow />

                <PopoverHeader>
                    <Flex>
                        <Input
                            placeholder="Search var name"
                            value={serachQuery}
                            type={'string'}
                            onChange={({ target: { value } }: any) => {
                                setSearchQuery(value);
                            }}
                        />
                    </Flex>
                </PopoverHeader>
                <PopoverBody>
                    <Stack maxH={300} overflow={'scroll'}>
                        {instance.pipeline.editorSettings.varsOrder.map((varId: string) => {
                            const varObj = instance.pipeline.vars[varId];
                            let highlight = false;
                            let varValue = instance.vars[varId];
                            if (changes[varId] !== undefined) {
                                highlight = true;
                                varValue = changes[varId];
                            }
                            if (varObj.name.indexOf(serachQuery) == -1) return null;
                            return (
                                <Flex key={varId} alignItems={'center'}>
                                    <Text mr={2}>{varObj.name}:</Text>{' '}
                                    {varObj.type == 'STRING' && (
                                        <>
                                            <Input
                                                value={varValue}
                                                bgColor={highlight ? 'orange.100' : 'transparent'}
                                                type={'string'}
                                                onChange={({ target: { value } }: any) => {
                                                    let newChanges = JSON.parse(JSON.stringify(changes));

                                                    if (instance.vars[varId] == value) {
                                                        delete newChanges[varId];
                                                    } else {
                                                        newChanges[varId] = value;
                                                    }
                                                    setChanges(newChanges);
                                                }}
                                            />
                                            {varValue?.includes('http') ? (
                                                <a href={varValue} target={'_blank'}>
                                                    <Button
                                                        backgroundColor="white"
                                                        _hover={{ color: '#ffcccb' }}
                                                        _focus={{
                                                            backgroundColor: 'white',
                                                        }}
                                                        _active={{
                                                            backgroundColor: 'white',
                                                        }}
                                                        color={'grey'}
                                                        leftIcon={<ExternalLinkIcon />}
                                                    />{' '}
                                                </a>
                                            ) : null}
                                        </>
                                    )}
                                    {varObj.type == 'REF' && (
                                        <Input
                                            value={varValue ? JSON.stringify(varValue) : 'NaN'}
                                            bgColor={highlight ? 'orange.100' : 'transparent'}
                                            type={'string'}
                                            onChange={({ target: { value } }: any) => {
                                                let newChanges = JSON.parse(JSON.stringify(changes));

                                                const arrayValue = JSON.parse(value);
                                                if (
                                                    JSON.stringify(instance.vars[varId]) == JSON.stringify(arrayValue)
                                                ) {
                                                    delete newChanges[varId];
                                                } else {
                                                    newChanges[varId] = arrayValue;
                                                }
                                                setChanges(newChanges);
                                            }}
                                        />
                                    )}
                                    {varObj.type == 'NUMBER' && (
                                        <NumberInput
                                            value={varValue}
                                            bgColor={highlight ? 'orange.100' : 'transparent'}
                                            ml={2}
                                            onChange={(value: any) => {
                                                let newChanges = JSON.parse(JSON.stringify(changes));

                                                if (instance.vars[varId] == value) {
                                                    delete newChanges[varId];
                                                } else {
                                                    newChanges[varId] = value;
                                                }
                                                setChanges(newChanges);
                                            }}
                                        >
                                            <NumberInputField />
                                            <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                            </NumberInputStepper>
                                        </NumberInput>
                                    )}
                                    {varObj.type == 'BOOLEAN' && (
                                        <Select
                                            width={300}
                                            ml={2}
                                            value={varValue ? 'true' : 'false'}
                                            bgColor={highlight ? 'orange.100' : 'transparent'}
                                            onChange={({ target: { value } }: any) => {
                                                let newChanges = JSON.parse(JSON.stringify(changes));
                                                const boolValue = value == 'true';
                                                if (instance.vars[varId] == boolValue) {
                                                    delete newChanges[varId];
                                                } else {
                                                    newChanges[varId] = boolValue;
                                                }
                                                setChanges(newChanges);
                                            }}
                                        >
                                            <option value="true">True</option>
                                            <option value="false">False</option>
                                        </Select>
                                    )}
                                </Flex>
                            );
                        })}
                    </Stack>
                </PopoverBody>
                <PopoverFooter>
                    <Center>
                        <Button
                            ml={2}
                            disabled={Object.keys(changes).length === 0}
                            onClick={() => {
                                setChanges({});
                            }}
                        >
                            Discard Changes
                        </Button>
                        <Button
                            colorScheme={'orange'}
                            ml={2}
                            disabled={Object.keys(changes).length === 0}
                            onClick={() => {
                                let newVars = JSON.parse(JSON.stringify(instance.vars));
                                Object.keys(changes).forEach((varId) => (newVars[varId] = changes[varId]));
                                updateInstance({
                                    variables: {
                                        instanceId: instance.id,
                                        dataSet: { vars: newVars },
                                    },
                                    onCompleted: () => {
                                        setChanges({});
                                    },
                                });
                            }}
                        >
                            Save Changes
                        </Button>
                    </Center>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    );
}
type InstanceLogsPopoverProps = { instance: PipelineInstance };
function InstanceLogsPopover({ instance }: InstanceLogsPopoverProps) {
    return (
        <Popover isLazy placement={'auto-end'}>
            <PopoverTrigger>
                <Button mr={2}>Logs</Button>
            </PopoverTrigger>
            <PopoverContent w={'100vh'}>
                <PopoverArrow />

                <PopoverHeader>
                    <Input
                        placeholder="Search log"
                        // value={
                        //     transition.startWindowTimeHours
                        // }
                        // w={55}
                        type={'number'}
                        onChange={({ target: { value } }: any) => {
                            // setTransitionProp(
                            //     "startWindowTimeHours",
                            //     value
                            // );
                        }}
                    />
                </PopoverHeader>
                <PopoverBody>
                    <Stack overflowY={'auto'} overflowX={'auto'} maxH={'400px'} scrollBehavior={'auto'} spacing={0}>
                        {instance.log.map(({ level, msg, ts, data }, index) => {
                            if (true) {
                                let levelColor = '#00000000';
                                if (level == 'error') {
                                    levelColor = 'red';
                                }
                                if (level == 'warn') {
                                    levelColor = '#d4d059';
                                }
                                return (
                                    <Text
                                        key={ts}
                                        // backgroundColor={"#25D366"}
                                        backgroundColor={
                                            data && data['presentationInLog'] === 'message'
                                                ? '#25D366'
                                                : index % 2 === 1
                                                ? 'lightgray'
                                                : 'white'
                                        }
                                        py={4}
                                    >
                                        {moment(new Date(ts)).format('DD/MM/YYYY HH:mm:ss')} [
                                        <span
                                            style={{
                                                backgroundColor: levelColor,
                                            }}
                                        >
                                            {level}
                                        </span>
                                        ] {msg} <br />
                                        {data && (
                                            <Box>
                                                {Object.keys(data).map((key) => (
                                                    <Box ml={5}>
                                                        <Flex>
                                                            <Text textColor={'grey'}>{key}:</Text>
                                                            <Stack>
                                                                {JSON.stringify(data[key])
                                                                    .split('\\n')
                                                                    .map((line) => (
                                                                        <Text>{line}</Text>
                                                                    ))}
                                                            </Stack>
                                                        </Flex>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Text>
                                );
                            } else {
                            }
                        })}
                    </Stack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
type InstanceModeratorOptionsPopoverProps = { instance: PipelineInstance };
function InstanceModeratorOptionsPopover({ instance }: InstanceModeratorOptionsPopoverProps) {
    const [updateInstance] = useMutation(UPDATE_INSTANCE_MUTATION, {
        onCompleted: (data) => {
            const instanceId = data?.response?.id;
            if (!instanceId) {
                alert("Error: didn't get id");
            }
        },
        onError: (err) => {
            alert(err);
        },
    });
    const step = instance.pipeline.steps[instance.currentStepId] as ModeratorInputStep;
    return (
        <Popover isLazy>
            <PopoverTrigger>
                <Button mr={2}>Options</Button>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />

                <PopoverBody>
                    <Stack overflow={'scroll'} scrollBehavior={'auto'}>
                        {step.valueOptions.map(({ btnLabel, targetValue }) => {
                            return (
                                <Button
                                    onClick={() => {
                                        let newVars = JSON.parse(JSON.stringify(instance.vars));

                                        newVars[step.targetVarId] = targetValue;
                                        updateInstance({
                                            variables: {
                                                instanceId: instance.id,
                                                dataSet: { vars: newVars },
                                            },
                                        });
                                    }}
                                    key={targetValue}
                                >
                                    {btnLabel}
                                </Button>
                            );
                        })}
                    </Stack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

type FastForwarModalProps = {
    instance: PipelineInstance | null;
    setInstance: any;
};
function FastForwardModal({ instance, setInstance }: FastForwarModalProps) {
    const [fastForwardAmounts, setFastForwardAmounts] = useState(0);
    const [fastForwardUnit, setFastForwardUnit] = useState('seconds');
    const toast = useToast();
    const [updateInstance] = useMutation(UPDATE_INSTANCE_MUTATION, {
        onCompleted: (data) => {
            const instanceId = data?.response?.id;
            if (!instanceId) {
                alert("Error: didn't get id");
            }
        },
        onError: (err) => {
            alert(err);
        },
    });

    return (
        <Modal
            isOpen={instance !== null}
            onClose={() => {
                setInstance(null);
                setFastForwardAmounts(0);
                setFastForwardUnit('seconds');
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Fast forawrd</ModalHeader>
                <ModalCloseButton
                    onClick={() => {
                        setFastForwardAmounts(0);
                        setFastForwardUnit('seconds');
                        setInstance(null);
                    }}
                />
                <ModalBody>
                    <Center>
                        <HStack>
                            <NumberInput
                                maxW={32}
                                value={fastForwardAmounts}
                                min={0}
                                onChange={(value) => setFastForwardAmounts(parseInt(value))}
                            >
                                <NumberInputField />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                            <Select
                                maxW={32}
                                value={fastForwardUnit}
                                onChange={({ target }: any) => setFastForwardUnit(target.value)}
                            >
                                <option value="seconds">Seconds</option>
                                <option value="minutes">Minutes</option>
                                <option value="hours">Hours</option>
                                <option value="days">Days</option>
                            </Select>
                            <Button
                                onClick={async () => {
                                    let ts = new Date().getTime();
                                    if (fastForwardUnit == 'seconds') {
                                        ts -= fastForwardAmounts * 1000;
                                    }
                                    if (fastForwardUnit == 'minutes') {
                                        ts -= fastForwardAmounts * 1000 * 60;
                                    }
                                    if (fastForwardUnit == 'hours') {
                                        ts -= fastForwardAmounts * 1000 * 60 * 60;
                                    }
                                    if (fastForwardUnit == 'days') {
                                        ts -= fastForwardAmounts * 1000 * 60 * 60 * 24;
                                    }

                                    updateInstance({
                                        variables: {
                                            instanceId: instance?.id,
                                            dataSet: {
                                                currentStepStartTime: new Date(ts),
                                            },
                                        },
                                        onCompleted: () => {
                                            setFastForwardAmounts(0);
                                            setFastForwardUnit('seconds');
                                            setInstance(null);

                                            toast({
                                                title: `Updated instance ${instance?.id} time`,
                                                position: 'bottom',
                                                status: 'info',
                                                isClosable: true,
                                            });
                                        },
                                        onError: () => {
                                            alert('Error');
                                        },
                                    });
                                }}
                                colorScheme="red"
                                isDisabled={fastForwardAmounts == 0}
                            >
                                Go
                            </Button>
                        </HStack>
                    </Center>
                </ModalBody>
                <ModalFooter>Make sure you know what's you are doing!</ModalFooter>
            </ModalContent>
        </Modal>
    );
}

type JumpToStepModalProps = {
    instance: PipelineInstance | null;
    setInstance: any;
};
function JumpToStepdModal({ instance, setInstance }: JumpToStepModalProps) {
    const [stepJumpStepId, setStepJumpStepId] = useState('');
    const toast = useToast();
    const [updateInstance] = useMutation(UPDATE_INSTANCE_MUTATION, {
        onCompleted: (data) => {
            const instanceId = data?.response?.id;
            if (!instanceId) {
                alert("Error: didn't get id");
            }
        },
        onError: (err) => {
            alert(err);
        },
    });
    return (
        <Modal
            isOpen={instance !== null}
            onClose={() => {
                setInstance(null);
                setStepJumpStepId('');
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Step jump</ModalHeader>
                <ModalCloseButton
                    onClick={() => {
                        setInstance(null);
                        setStepJumpStepId('');
                    }}
                />
                <ModalBody>
                    <Center>
                        <HStack>
                            <Select
                                value={stepJumpStepId}
                                onChange={({ target }: any) => setStepJumpStepId(target.value)}
                            >
                                <option value="">Select step</option>
                                {instance &&
                                    Object.keys(instance.pipeline.steps).map((stepId) => {
                                        const step = instance.pipeline.steps[stepId];
                                        return (
                                            <option key={stepId} value={stepId}>
                                                {step.name}
                                            </option>
                                        );
                                    })}
                            </Select>
                            <Button
                                onClick={async () => {
                                    updateInstance({
                                        variables: {
                                            instanceId: instance?.id,
                                            dataSet: {
                                                currentStepId: stepJumpStepId,
                                                status: 'jumped',
                                            },
                                        },
                                        onCompleted: () => {
                                            toast({
                                                title: `jumped to step ${stepJumpStepId}`,
                                                position: 'bottom',
                                                status: 'info',
                                                isClosable: true,
                                            });
                                            setInstance(null);
                                            setStepJumpStepId('');
                                        },
                                        onError: () => {
                                            alert('Error');
                                        },
                                    });
                                }}
                                colorScheme="red"
                                isDisabled={stepJumpStepId == ''}
                            >
                                Jump
                            </Button>
                        </HStack>
                    </Center>
                </ModalBody>
                <ModalFooter>Make sure you know what's you are doing!</ModalFooter>
            </ModalContent>
        </Modal>
    );
}

type InstancesTableProps = { instances: PipelineInstance[]; deleteInstance?: any };
export function InstancesTable({ instances, deleteInstance }: InstancesTableProps) {
    const [fastForwardfInstance, setFastForwardfInstance] = useState(null);
    const [jumpToStepInstance, setJumpToStepInstance] = useState(null);
    return (
        <>
            <FastForwardModal instance={fastForwardfInstance} setInstance={setFastForwardfInstance} />
            <JumpToStepdModal instance={jumpToStepInstance} setInstance={setJumpToStepInstance} />

            <TableContainer>
                <Table whiteSpace={'nowrap'}>
                    <Thead>
                        <Tr>
                            <Th w={1}></Th>
                            <Th w={1}>Instance id</Th>
                            <Th w={1}>Status</Th>
                            <Th w={1}>Current Step</Th>
                            <Th></Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {instances.map((instance: any) => {
                            return (
                                <Tr key={instance.id}>
                                    <Td>
                                        <Menu isLazy>
                                            <MenuButton
                                                as={IconButton}
                                                aria-label="Options"
                                                // icon={
                                                //     <WarningTwoIcon />
                                                // }
                                                variant="outline"
                                                // color={"red.400"}
                                            >
                                                =
                                            </MenuButton>
                                            <MenuList>
                                                <MenuItem
                                                    icon={<TimeIcon />}
                                                    onClick={() => {
                                                        setFastForwardfInstance(instance);
                                                    }}
                                                >
                                                    Fast forward
                                                </MenuItem>

                                                <MenuItem
                                                    icon={<CalendarIcon />}
                                                    onClick={() => {
                                                        setJumpToStepInstance(instance);
                                                    }}
                                                >
                                                    Jump to step
                                                </MenuItem>

                                                {deleteInstance && (
                                                    <MenuItem
                                                        icon={<DeleteIcon />}
                                                        onClick={async () => {
                                                            if (
                                                                window.confirm(
                                                                    'Are you sure you want to DELETE this instance?'
                                                                ) == true
                                                            ) {
                                                                deleteInstance({
                                                                    variables: {
                                                                        instanceId: instance.id,
                                                                    },
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        Delete instance
                                                    </MenuItem>
                                                )}
                                            </MenuList>
                                        </Menu>
                                    </Td>
                                    <Td>{instance.id}</Td>
                                    <Td>{instance.status}</Td>
                                    <Td>{instance.pipeline?.steps[instance.currentStepId]?.name}</Td>
                                    <Td>
                                        <InstanceVarsPopover instance={instance} />
                                        <InstanceLogsPopover instance={instance} />
                                        {instance.pipeline.steps[instance.currentStepId]?.type == 'MODERATOR_INPUT' && (
                                            <InstanceModeratorOptionsPopover instance={instance} />
                                        )}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>
        </>
    );
}
